import React from 'react';
import PropTypes from 'prop-types';

import validDragEvents from './validDragEvents';
import GapChoice from './GapChoice';
import LoadTracker from '../../../../services/LoadTracker';
import styles from './gapMatchInteraction.scss';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import { t } from '../../../../services/i18nService';
import questionReader from '../../../../store/readHelpers/QuestionReader';
import GapMatchInteractionServices from '../../../../services/questionView/GapMatchInteractionServices';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import HiddenItemFlagButton from '../../itemFlag/HiddenItemFlagButton';
import themeServices from '../../../../themes/ThemeServices';

class Gap extends React.Component {
    static propTypes = {
        gapModel: PropTypes.object.isRequired,
        gapChoiceModels: PropTypes.array,
        gapWidth: PropTypes.number.isRequired,
        gapHeight: PropTypes.number.isRequired,
        handleUserResponse: PropTypes.func.isRequired,
        reportReady: PropTypes.func.isRequired,
        orderNumber: PropTypes.string,
        services: PropTypes.object,
        questionModel: PropTypes.object,
        model: PropTypes.object,
        isActive: PropTypes.bool,
        isFlagged: PropTypes.bool,
        extendedRenderingActivated: PropTypes.bool,
        gapToGapTextMap: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = { dragOver: false, liveMessage: '', tokenGapMessage: '' };
        this.readyTracker = new LoadTracker(props.reportReady, `Gap ${props.gapModel.identifier}`);
        this.gapChoiceTrackers = props.gapChoiceModels
            .reduce((trackers, gapChoiceModel) => {
                trackers[gapChoiceModel.identifier] = this.readyTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier}`);
                return trackers;
            }, {});
        this.readyTracker.setAllItemsAdded();
        this.navigateToScorableItem = this.navigateToScorableItem.bind(this);
    }

    getDraggedGapTextIdentifier = (event) => {
        try {
            return JSON.parse(event.dataTransfer.getData('text'));
        } catch (error) {
            return {};
        }
    };

    isValidDragEvent = (event) => {
        return validDragEvents.indexOf(event) !== -1;
    };

    onDragEnter = (event) => {
        if (this.isValidDragEvent(event)) {
            event.preventDefault();
            this.setState({ dragOver: true });
        }
    };

    onDragOver = (event) => {
        if (this.isValidDragEvent(event)) {
            event.preventDefault();
        }
    };

    onDragLeave = (event) => {
        event.preventDefault();
        this.setState({ dragOver: false });
    };

    onDrop = (event) => {
        const { gapIdentifier, gapTextIdentifier, questionId, modelIdentifier } = this.getDraggedGapTextIdentifier(event);
        if (gapTextIdentifier) {
            delete validDragEvents[event.timestamp];
            event.preventDefault();
            const { gapModel, handleUserResponse, questionModel } = this.props;

            if (questionModel.contentRevisionId === questionId) {
                handleUserResponse(gapTextIdentifier, gapModel.identifier, gapIdentifier, modelIdentifier);
                this.navigateToScorableItem();
            }

            this.setState({ dragOver: false });
            const gapIdentifierElement = document.querySelectorAll(`[data-identifier=${gapTextIdentifier}]`)[0];
            if (gapIdentifierElement) {
                gapIdentifierElement.classList.remove('dragging');
            }

            if (event.target) {
                this.setLiveMessageData(event.target.closest('p'), gapTextIdentifier);
            }
        }
    };

    setLiveMessageData(closestPTag, gapTextIdentifier) {
        if (!closestPTag || !gapTextIdentifier) return;

        let tokenGapMessage;
        const { gapModel, gapToGapTextMap } = this.props;

        const liveMessage = Array.from(closestPTag.childNodes).map((node) => {
            let dataToReturn = '';
            if (node.nodeType === Node.TEXT_NODE) {
                dataToReturn = node.textContent;
            } else if (node.nodeName === 'SPAN') {
                const gapIdentifier = node.querySelector('div[id^="scorableItem"]')?.getAttribute('data-identifier');
                const tokenData = document.querySelector(`[data-identifier="${gapTextIdentifier}"]`)?.textContent;

                const dataForGap = gapToGapTextMap?.[gapIdentifier];

                if (dataForGap && gapModel.identifier !== gapIdentifier) {
                    dataToReturn = document.querySelector(`[data-identifier="${dataForGap[0]}"]`)?.textContent || '';
                } else {
                    dataToReturn = gapModel.identifier === gapIdentifier ? (tokenData || '') : (gapIdentifier || '');
                }

                if (gapModel.identifier === gapIdentifier) {
                    tokenGapMessage = t('generic.ariaLive.tokenGapMessage', { tokenData, gapIdentifier });
                }
            }

            return (dataToReturn || '').trim();
        }).join(' ');

        this.setState({ tokenGapMessage, liveMessage: t('generic.ariaLive.fullParagraph', { liveMessage }) });
    }

    navigateToScorableItem(event) {
        const { questionModel, model, gapModel } = this.props;
        scorableItemNumberingService.navigateToScorableItem(questionModel.contentRevisionId, model.identifier, gapModel.identifier, true);

        if (event) {
            const gapTextIdentifier = event.target.closest('p')
                ?.querySelector('div[id^="scorableItem"]')
                ?.childNodes[0]
                ?.getAttribute('data-identifier');

            this.setLiveMessageData(event.target.closest('p'), gapTextIdentifier);
        }
    }

    doubleClickHandler = (event) => {
        const { gapModel, handleUserResponse } = this.props;
        const gapTextIdentifier = event.target.dataset.identifier;
        handleUserResponse(gapTextIdentifier, undefined, gapModel.identifier);
    };

    gerOrderNumber() {
        const { gapChoiceModels, orderNumber } = this.props;
        if (!gapChoiceModels.length) {
            return (
                <span className="gapOrderNumber">{orderNumber}</span>
            );
        }
        return null;
    }

    render() {
        const {
            questionModel,
            gapModel,
            gapChoiceModels,
            gapHeight,
            gapWidth,
            isActive,
            isFlagged,
            orderNumber,
            extendedRenderingActivated,
            model,
        } = this.props;
        const isMultipleInteractionsGapMatchQuestion = questionReader.isGapMatchQuestionWithMultipleInteractions(questionModel);
        const shouldAutoSizeTokens = Boolean(model?.shouldAutoSizeTokens);
        const tokenPosition = model?.tokenPosition || 'top';
        const isRightPinnedLayout = ['right', 'rightWithSplitPreview'].includes(tokenPosition);
        const isPopulatedWithToken = gapChoiceModels.length > 0 && !GapChoice.showImage(gapChoiceModels[0]);
        const measuredGapWidth = (Number.isFinite(gapWidth) && gapWidth > 0) ? gapWidth : 0.3 * window.innerWidth;
        const isFjordBodyTheme = typeof document !== 'undefined' && document.body?.classList.contains('fjord-theme');
        const isDefaultTheme = !themeServices.isCeq() && !isFjordBodyTheme;
        let appliedStyles = {};
        if (isMultipleInteractionsGapMatchQuestion) {
            if (isDefaultTheme) {
                appliedStyles = {
                    ...GapMatchInteractionServices.getAutoSizeTokenStyles(gapWidth, shouldAutoSizeTokens),
                };
            } else {
                appliedStyles = { width: `${measuredGapWidth + 3}px` };
            }
        } else {
            appliedStyles = { width: `${gapWidth + 3}px` };
            if (!marketplacePropertiesService.isEnableTwoInlineGapMatchAreas()) {
                appliedStyles.height = `${gapHeight + 2}px`;
            }
        }
        const { imageHeight, imageWidth } = GapMatchInteractionServices.getImageTokenDimensions(gapChoiceModels?.[0]);
        if (imageHeight && imageWidth && isMultipleInteractionsGapMatchQuestion) {
            appliedStyles.height = `${imageHeight}px`;
            appliedStyles.width = shouldAutoSizeTokens && isDefaultTheme ? appliedStyles.width : `${imageWidth}px`;
        }
        if (isRightPinnedLayout && isDefaultTheme) {
            const hasToken = gapChoiceModels.length > 0;
            if (shouldAutoSizeTokens) {
                appliedStyles.width = `${measuredGapWidth + 3}px`;
                appliedStyles.maxWidth = '100%';
                if (isPopulatedWithToken) {
                    appliedStyles.height = 'auto';
                } else if (!hasToken) {
                    delete appliedStyles.height;
                }
            } else if (isPopulatedWithToken) {
                appliedStyles.height = 'auto';
                appliedStyles.width = '100%';
                delete appliedStyles.maxWidth;
            } else if (!hasToken) {
                appliedStyles.width = '100%';
                delete appliedStyles.height;
                delete appliedStyles.maxWidth;
            }
        } else if (isMultipleInteractionsGapMatchQuestion && isPopulatedWithToken && isDefaultTheme) {
            appliedStyles.width = `${measuredGapWidth + 3}px`;
            appliedStyles.height = 'auto';
        } else if (isMultipleInteractionsGapMatchQuestion && !shouldAutoSizeTokens && gapChoiceModels.length === 0 && isDefaultTheme) {
            // Keep empty gaps aligned with the measured token width to avoid collapsing to just the order-number width
            appliedStyles.minWidth = appliedStyles.maxWidth;
        }
        const isPageBreakingPrintView = settingsReader.shouldPageBreakOnPrintView();
        const style = (extendedRenderingActivated && !isMultipleInteractionsGapMatchQuestion) ? undefined : appliedStyles;
        const isA11ySupportedForV4AndAbove = GapMatchInteractionServices.isA11ySupportedForV4AndAbove();
        const a11yAttributes = isA11ySupportedForV4AndAbove ? {
            'role': 'region',
            'aria-roledescription': t('ariaRoleDescription.dropZone'),
            'tabIndex': undefined,
            'onFocus': undefined,

        } : {
            'role': 'button',
            'aria-roledescription': undefined,
            'tabIndex': 0,
            'onFocus': this.navigateToScorableItem,
        };

        const classList = [styles.dropZone, 'gap'];
        if (isActive) {
            classList.push('active');
        }
        if (isFlagged) {
            classList.push('flagged');
        }
        if (gapChoiceModels.length) {
            classList.push('populated');
        }
        if (this.state.dragOver) {
            classList.push('drag-over');
        }
        if (extendedRenderingActivated && !isMultipleInteractionsGapMatchQuestion) {
            classList.push('extendedGap');
        }
        if (extendedRenderingActivated && gapChoiceModels.length && !isMultipleInteractionsGapMatchQuestion) {
            classList.push('extendTargetGapWidth');
        }
        if (isFlagged) {
            classList.push('flagged');
        }
        if (isPageBreakingPrintView) {
            classList.push(styles.breakingPageView);
        }

        return (
            <React.Fragment>
                {/* Aria-live region for screen reader announcements */}
                <div className="sr-only" aria-live="assertive">
                    {this.state.tokenGapMessage}
                    {this.state.liveMessage}
                </div>
                <div
                    className={classList.join(' ')}
                    data-type="drop"
                    data-identifier={gapModel.identifier}
                    onDragEnter={this.onDragEnter}
                    onDragOver={this.onDragOver}
                    onDragLeave={this.onDragLeave}
                    onDrop={this.onDrop}
                    onDoubleClick={this.doubleClickHandler}
                    style={style}
                    id={orderNumber ? `scorableItem-${questionModel.contentRevisionId}_${orderNumber}` : undefined}
                    {...a11yAttributes}
                >
                    {this.gerOrderNumber()}
                    {
                        gapChoiceModels.map((gapChoiceModel) => {
                            return (
                                <GapChoice
                                    {...this.props}
                                    gapChoiceModel={gapChoiceModel}
                                    gapModel={gapModel}
                                    key={gapChoiceModel.identifier}
                                    reportReady={this.gapChoiceTrackers[gapChoiceModel.identifier]}
                                    shouldAutoSizeTokens={shouldAutoSizeTokens}
                                    isInsideGap
                                />
                            );
                        })
                    }
                </div>
                {
                    // Add a hidden focusable flag button after the gap so Tab moves there naturally
                    scorableItemNumberingService.shouldAddHiddenFlagFromSettings() &&
                        <HiddenItemFlagButton
                            orderNumber={orderNumber}
                            responseIdentifier={model.identifier}
                            contentRevisionId={questionModel.contentRevisionId}
                            choiceIdentifier={gapModel.identifier}
                        />
                }
            </React.Fragment>
        );
    }
}

export default Gap;
